import { StripeError } from '@stripe/stripe-js';
import { ZefHttpErrorResponse } from '@vshosting/core';

/**
 * Interfaces for API requests and responses
 */

export interface CreateIntentPayload {
  clientId: string;
  amount: number;
  type: IntentTypes;
}

export enum IntentTypes {
  INVOICE = 'INVOICE',
  FEE = 'FEE',
  CREDIT = 'CREDIT'
}

export enum PaymentStatus {
  UNKNOWN_CLIENT = 'UNKNOWN_CLIENT',
  AMOUNT_NOT_MATCHED = 'AMOUNT_NOT_MATCHED',
  OK = 'OK',
  CANCELED = 'CANCELED',
  BEING_PROCESSED = 'BEING_PROCESSED',
  IGNORED = 'IGNORED'
}

export interface PaymentStatusCheckResponse {
  id: string;
  status: PaymentStatus;
}

export interface StripeIntentResponse {
  clientSecret: string;
}


/**
 * Type for Store state
 */
export type StripePaymentsState = { [key: string]: PaymentProcessState }


/**
 * Represents various reasons for how and why a payment process can fail.
 */
export enum PaymentFailureReason {

  /**
   * Stripe failure: could not confirm the payment - response was valid, but contained an error.
   */
  ConfirmPaymentError = 'CONFIRM_PAYMENT_ERROR',

  /**
   * Stripe failure: call to Stripe API failed.
   */
  ConfirmPaymentFailed = 'CONFIRM_PAYMENT_FAILED',

  /**
   * Internal failure: API returned an error.
   */
  PaymentCheckError = 'PAYMENT_CHECK_ERROR',

  /**
   * Internal failure: API request failed.
   */
  PaymentCheckFailed = 'PAYMENT_CHECK_FAILED',

  /**
   * *Possible* failure: API could not find the payment in time.
   */
  WaitingTimeout = 'WAITING_TIMEOUT',
  
  /**
   * *Possible* failure: the payment took too long to process.
   */
  ProcessingTimeout = 'PROCESSING_TIMEOUT'
}

export type TimeoutFailureReason = PaymentFailureReason.WaitingTimeout | PaymentFailureReason.ProcessingTimeout;

/**
 * Constructing a type to represent possible failures of the payment process with metadata involved
 */

interface PaymentCheckErrorFailure {
  reason: PaymentFailureReason.PaymentCheckError;
  meta: PaymentStatusCheckResponse
}

interface PaymentCheckFailedFailure {
  reason: PaymentFailureReason.PaymentCheckFailed;
  meta: ZefHttpErrorResponse;
}

interface ConfirmPaymentErrorFailure {
  reason: PaymentFailureReason.ConfirmPaymentError;
  meta: StripeError;
}

interface ConfirmPaymentFailedResult {
  reason: PaymentFailureReason.ConfirmPaymentFailed;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: any;
}

export type PaymentFailureType =
  | PaymentCheckErrorFailure
  | PaymentCheckFailedFailure
  | ConfirmPaymentErrorFailure
  | ConfirmPaymentFailedResult

/**
 * Tells what is currently happening with a payment
 */
export enum PaymentProcessStatus {
  /**
   * Means payment is currently being confirmed with Stripe API and is subject to change.
   */
  AwaitingConfirmation = 'AWAITING_CONFIRMATION',

  /**
   * Means Stripe confirmed the payment and it is now being checked with our API.
   */
  CheckingPayment = 'CHECKING_PAYMENT',

  /**
   * Payment successfully completed.
   */
  Completed = 'COMPLETED',

  /**
   * Payment unrecoverably failed.
   */
  Failed = 'FAILED',

  /**
   * Payment could not be confirmed within a reasonable time, so we stopped trying; does not mean a definitive failure.
   */
  Unresolved = 'UNRESOLVED'
}

/**
 * Following is various types to represent the payment state in the store - basically pairing each value from the enum above with
 * a specific metadata to contain all the information available and/or required.
 */

interface AwaitingConfirmationState {
  status: PaymentProcessStatus.AwaitingConfirmation;
  clientSecretPublicPart: string;
}

export interface CheckingPaymentState {
  status: PaymentProcessStatus.CheckingPayment;
  waitingTriesRemaining: number;
  processingTriesRemaining: number;
  clientSecretPublicPart: string;
}

interface CompletedPaymentState {
  status: PaymentProcessStatus.Completed;
}

interface UnresolvedPaymentState {
  status: PaymentProcessStatus.Unresolved;
  failure: {
    reason: PaymentFailureReason.ProcessingTimeout | PaymentFailureReason.WaitingTimeout;
  }
}

export interface FailedPaymentState {
  status: PaymentProcessStatus.Failed;
  failure: PaymentFailureType;
}

export type PaymentProcessState = 
  | AwaitingConfirmationState
  | CheckingPaymentState
  | CompletedPaymentState
  | UnresolvedPaymentState
  | FailedPaymentState;
